import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Video, VideoOff, SwitchCamera, PhoneOff, Users } from 'lucide-react';
import { useConference } from '../services/conference';
import '../styles/Conference.css';

// Сетка под число окон (вместе со своим): 4 окна → 2×2
function gridFor(n, landscape) {
  if (n <= 1) return { cols: 1, rows: 1 };
  if (n === 2) return landscape ? { cols: 2, rows: 1 } : { cols: 1, rows: 2 };
  if (n <= 4) return { cols: 2, rows: 2 };
  if (n <= 6) return landscape ? { cols: 3, rows: 2 } : { cols: 2, rows: 3 };
  return { cols: 3, rows: Math.ceil(n / 3) };
}

function initials(name) {
  return (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

function useLandscape() {
  const [land, setLand] = useState(window.innerWidth > window.innerHeight);
  useEffect(() => {
    const on = () => setLand(window.innerWidth > window.innerHeight);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return land;
}

function Tile({ stream, name, self, mirror, audioOn, videoOn, connecting, onBlocked }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (v.srcObject !== stream) v.srcObject = stream || null;
    if (stream) {
      const p = v.play();
      if (p && p.catch) p.catch(() => { if (!self) onBlocked(); });
    }
  }, [stream, self, onBlocked]);

  const showVideo = stream && videoOn;
  return (
    <div className="conf-tile">
      <video
        ref={ref}
        autoPlay
        playsInline
        muted={self}
        className={mirror ? 'mirror' : ''}
        style={{ visibility: showVideo ? 'visible' : 'hidden' }}
      />
      {!showVideo && <div className="conf-avatar">{initials(name)}</div>}
      {connecting && <div className="conf-tile-status">Подключение…</div>}
      <div className="conf-tile-name">
        {!audioOn && <MicOff size={14} />}
        <span>{self ? `${name} (вы)` : name}</span>
      </div>
    </div>
  );
}

function Timer({ from }) {
  const [, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT(x => x + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const s = Math.max(0, Math.floor((Date.now() - from) / 1000));
  const hh = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return <span>{hh ? `${hh}:` : ''}{mm}:{ss}</span>;
}

function ConferenceCall({ user }) {
  const { call } = useConference();
  const landscape = useLandscape();
  const [blocked, setBlocked] = useState(false);
  const onBlocked = useRef(() => setBlocked(true)).current;
  const rootRef = useRef(null);

  if (!call) return null;

  if (call.status === 'error' || call.status === 'ended') {
    return (
      <div className="conf-overlay conf-overlay-message">
        <div className="conf-message-box">
          <div className="conf-message-icon">{call.status === 'error' ? '⚠️' : '🎥'}</div>
          <p>{call.error || call.endReason || 'Вы вышли из конференции'}</p>
          <button className="conf-btn-primary" onClick={() => call.dismiss()}>Закрыть</button>
        </div>
      </div>
    );
  }

  const startedAt = call.conference?.startedAt ? new Date(call.conference.startedAt).getTime() : call.startedAt;
  const others = [...call.peers.values()];
  const tiles = [
    ...others.map(e => ({
      key: e.info.peerId,
      stream: e.stream,
      name: e.info.displayName,
      audioOn: e.info.audio !== false,
      videoOn: e.info.video !== false,
      connecting: !e.stream || (e.state !== 'connected' && e.state !== 'disconnected'),
    })),
    {
      key: 'self', self: true, stream: call.localStream, name: user.displayName,
      audioOn: call.audioOn, videoOn: call.videoOn, mirror: call.facing === 'user',
    },
  ];
  const { cols, rows } = gridFor(tiles.length, landscape);

  const unblock = () => {
    rootRef.current?.querySelectorAll('video').forEach(v => v.play().catch(() => {}));
    setBlocked(false);
  };

  const confirmEnd = () => {
    if (window.confirm('Завершить конференцию для всех участников?')) call.endForAll();
  };

  return (
    <div className="conf-overlay" ref={rootRef}>
      <div className={`conf-topbar ${call.isHost ? 'with-end' : ''}`}>
        <span className="conf-title">🎥 <Timer from={startedAt} /> · Конференция</span>
        <span className="conf-count"><Users size={14} /> {tiles.length}</span>
        {call.recording && <span className="conf-rec">● Запись</span>}
        {call.status === 'connecting' && <span className="conf-connecting">Вход…</span>}
      </div>

      <div
        className="conf-grid"
        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` }}
      >
        {tiles.map(t => <Tile key={t.key} {...t} onBlocked={onBlocked} />)}
      </div>

      {others.length === 0 && call.status === 'live' && (
        <div className="conf-waiting">Ждём участников… Пока можно поправить камеру.</div>
      )}

      {blocked && (
        <button className="conf-unblock" onClick={unblock}>🔊 Нажмите, чтобы включить звук</button>
      )}

      <div className="conf-controls">
        <button className={`conf-ctl ${call.audioOn ? '' : 'off'}`} onClick={() => call.toggleAudio()} title="Микрофон">
          {call.audioOn ? <Mic size={22} /> : <MicOff size={22} />}
        </button>
        <button className={`conf-ctl ${call.videoOn ? '' : 'off'}`} onClick={() => call.toggleVideo()} title="Камера">
          {call.videoOn ? <Video size={22} /> : <VideoOff size={22} />}
        </button>
        {call.videoOn && (
          <button className="conf-ctl" onClick={() => call.switchCamera()} title="Другая камера">
            <SwitchCamera size={22} />
          </button>
        )}
        {call.isHost && (
          <button
            className={`conf-ctl conf-ctl-rec ${call.recording ? 'on' : ''}`}
            onClick={() => call.setRecording(!call.recording)}
            title={call.recording ? 'Остановить запись' : 'Включить запись'}
          >
            <span className="conf-rec-dot" />
          </button>
        )}
        <button className="conf-ctl conf-ctl-leave" onClick={() => call.leave()} title="Выйти">
          <PhoneOff size={22} />
        </button>
      </div>
      {call.isHost && (
        <button className="conf-end-all" onClick={confirmEnd}>Завершить для всех</button>
      )}
    </div>
  );
}

export default ConferenceCall;
